import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { OffboardingPanel } from "@/components/offboarding/OffboardingPanel";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/DatePicker";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import { FloatField, FloatWrapper } from "@/components/ui/FormField";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmployeeCombobox } from "@/components/ui/EmployeeCombobox";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";
import {
  Plus, Users, ClipboardList, BookOpen, LayoutDashboard,
  Play, Trash2, Edit, Eye, ChevronRight, Clock, CheckCircle2,
  AlertCircle, Pause, FileText, GripVertical, UserPlus, LogOut,
  ExternalLink, Paperclip, Save, Upload, X, File, TrendingUp, Info, Sparkles,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  OnboardingTemplate, OnboardingTemplateTask, OnboardingSession, OnboardingTask,
  OnboardingKBArticle, OnboardingStats, HRDocument, DocumentTemplate, DocumentTemplateBundle,
  apiGetOnboardingTemplates, apiCreateOnboardingTemplate, apiUpdateOnboardingTemplate, apiDeleteOnboardingTemplate,
  apiGetTemplateTasks, apiCreateTemplateTask, apiUpdateTemplateTask, apiDeleteTemplateTask,
  apiGetOnboardingSessions, apiCreateOnboardingSession, apiGetOnboardingSession,
  apiUpdateOnboardingSession, apiDeleteOnboardingSession,
  apiUpdateOnboardingTask, apiAddOnboardingTask,
  apiGetOnboardingKB, apiCreateOnboardingKBArticle, apiUpdateOnboardingKBArticle, apiDeleteOnboardingKBArticle,
  apiUploadKBFiles, apiDeleteKBFile,
  apiGetOnboardingStats, apiGetEmployees, EmployeeListItem,
  apiGetDocumentTemplates, apiGetDefaultDocumentTemplateBundle,
  apiGetHRDocument,
  apiGetDepartments, type Department,
} from "@/lib/api";
import { PageHeaderSkeleton, StatCardsSkeleton, CardGridSkeleton } from "@/components/PageSkeleton";
import { formatOnboardingDate } from "@/lib/onboarding-format";

const CATEGORIES = ["paperwork", "training", "setup", "introduction", "compliance", "custom"] as const;
const ASSIGNEE_TYPES = ["new_hire", "hr", "manager", "buddy"] as const;
const TASK_TYPES = ["task", "document_upload", "meeting"] as const;
const CHART_COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

const categoryConfig: Record<string, { color: string }> = {
  paperwork: { color: "bg-blue-100 text-blue-700" },
  training: { color: "bg-green-100 text-green-700" },
  setup: { color: "bg-purple-100 text-purple-700" },
  introduction: { color: "bg-amber-100 text-amber-700" },
  compliance: { color: "bg-red-100 text-red-700" },
  custom: { color: "bg-gray-100 text-gray-700" },
};

const taskTypeConfig: Record<string, { icon: typeof FileText; color: string }> = {
  task: { icon: CheckCircle2, color: "bg-slate-100 text-slate-700" },
  document_upload: { icon: Upload, color: "bg-blue-100 text-blue-700" },
  meeting: { icon: Users, color: "bg-orange-100 text-orange-700" },
};

const statusConfig: Record<string, { color: string; icon: typeof CheckCircle2 }> = {
  active: { color: "bg-green-100 text-green-700", icon: Play },
  completed: { color: "bg-blue-100 text-blue-700", icon: CheckCircle2 },
  paused: { color: "bg-amber-100 text-amber-700", icon: Pause },
  cancelled: { color: "bg-red-100 text-red-700", icon: AlertCircle },
};

export default function Onboarding() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const p = searchParams.get("tab");
    return p === "offboarding" || p === "templates" || p === "sessions" || p === "kb" ? p : "dashboard";
  });
  const [sessionFilter, setSessionFilter] = useState<"all" | "active" | "completed" | "paused" | "cancelled">("all");
  const [stats, setStats] = useState<OnboardingStats | null>(null);
  const [templates, setTemplates] = useState<OnboardingTemplate[]>([]);
  const [sessions, setSessions] = useState<OnboardingSession[]>([]);
  const [kbArticles, setKbArticles] = useState<OnboardingKBArticle[]>([]);
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [documentTemplates, setDocumentTemplates] = useState<DocumentTemplate[]>([]);
  const [defaultDocumentBundle, setDefaultDocumentBundle] = useState<DocumentTemplateBundle | null>(null);
  const [loading, setLoading] = useState(true);

  // Sheets / dialogs
  const [templateSheet, setTemplateSheet] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<OnboardingTemplate | null>(null);
  const [templateTasks, setTemplateTasks] = useState<OnboardingTemplateTask[]>([]);
  const [sessionSheet, setSessionSheet] = useState(false);
  const [selectedSession, setSelectedSession] = useState<OnboardingSession | null>(null);
  const [newSessionDialog, setNewSessionDialog] = useState(false);
  const [newTemplateDialog, setNewTemplateDialog] = useState(false);

  // Auto-open new session dialog when navigated from Quick Actions
  useEffect(() => {
    if (searchParams.get("action") === "create") {
      setTab("sessions");
      setNewSessionDialog(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [kbSheet, setKbSheet] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<OnboardingKBArticle | null>(null);
  const [taskDialog, setTaskDialog] = useState(false);

  // Forms
  const [tmplForm, setTmplForm] = useState({ name: "", description: "", targetRole: "", targetDepartment: "" });
  const [sessionForm, setSessionForm] = useState({ employeeId: 0, templateId: 0, startDate: "", buddyId: 0, notes: "" });
  const [sessionDocumentOverrideEnabled, setSessionDocumentOverrideEnabled] = useState(false);
  const [sessionDocumentTemplateIds, setSessionDocumentTemplateIds] = useState<number[]>([]);
  const [taskForm, setTaskForm] = useState({ title: "", description: "", category: "custom", dayOffset: 0, isRequired: true, assigneeType: "new_hire" as string, taskType: "task" as string, documentTemplateIds: [] as number[], sortOrder: 0 });
  const [editingTask, setEditingTask] = useState<OnboardingTemplateTask | null>(null);
  const [editTemplateMode, setEditTemplateMode] = useState(false);
  const [tmplEditForm, setTmplEditForm] = useState({ name: "", description: "", targetRole: "", targetDepartment: "" });
  const [kbForm, setKbForm] = useState({ title: "", content: "", category: "", tags: "", isPublished: true });
  const [kbUploading, setKbUploading] = useState(false);
  const [adHocTaskForm, setAdHocTaskForm] = useState({ title: "", description: "", category: "custom", dueDate: "", assigneeType: "new_hire" });

  // Task detail dialog (admin)
  const [taskDetailOpen, setTaskDetailOpen] = useState(false);
  const [activeAdminTask, setActiveAdminTask] = useState<OnboardingTask | null>(null);
  const [adminTaskNotes, setAdminTaskNotes] = useState("");
  const [adminTaskStatus, setAdminTaskStatus] = useState("pending");
  const [adminTaskDocs, setAdminTaskDocs] = useState<HRDocument[]>([]);
  const [adminTaskSaving, setAdminTaskSaving] = useState(false);

  // Animated counters
  const [animatedStats, setAnimatedStats] = useState({ active: 0, completed: 0, rate: 0, avg: 0 });

  const categoryLabel = (category: string) => t(`onboarding.category${category.charAt(0).toUpperCase()}${category.slice(1)}`, category);
  const taskTypeLabel = (taskType: string) => ({
    task: t("onboarding.taskTypeTask"),
    document_upload: t("onboarding.taskTypeDocumentUpload"),
    meeting: t("onboarding.taskTypeMeeting"),
  })[taskType] || taskType;
  const assigneeLabel = (assigneeType: string) => ({
    new_hire: t("onboarding.assigneeNewHireAdmin"),
    hr: t("onboarding.assigneeHr"),
    manager: t("onboarding.assigneeManager"),
    buddy: t("onboarding.assigneeBuddy"),
  })[assigneeType] || assigneeType;
  const statusLabel = (status: string) => ({
    active: t("onboarding.statusActive"),
    completed: t("onboarding.statusCompleted"),
    paused: t("onboarding.statusPaused"),
    cancelled: t("onboarding.statusCancelled"),
  })[status] || status;
  const formattedDate = (value: string | Date | null | undefined) => formatOnboardingDate(value, i18n.resolvedLanguage || i18n.language);
  const taskErrorMessage = (error: any) => {
    if (error?.code === "ONBOARDING_REQUIRED_DOCUMENT_MISSING") return t("onboarding.requiredDocumentMissing");
    if (error?.code === "ONBOARDING_REQUIRED_DOCUMENT_INCOMPLETE") return t("onboarding.requiredDocumentIncomplete");
    if (error?.code === "ONBOARDING_DOCUMENT_TEMPLATE_ALREADY_LINKED") return t("onboarding.documentTemplateAlreadyLinked");
    if (error?.code === "ONBOARDING_LINE_MANAGER_MISSING") return t("onboarding.lineManagerMissing");
    if (error?.code === "ONBOARDING_BUDDY_MISSING") return t("onboarding.buddyMissing");
    if (error?.code === "ONBOARDING_HR_ASSIGNEE_MISSING") return t("onboarding.hrAssigneeMissing");
    if (error?.code === "ONBOARDING_TASK_FORBIDDEN") return t("onboarding.taskUpdateForbidden");
    return error?.message || t("common.error");
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [s, tmpl, sess, kb, emps, depts, docTemplates, docBundle] = await Promise.all([
        apiGetOnboardingStats(),
        apiGetOnboardingTemplates(),
        apiGetOnboardingSessions(),
        apiGetOnboardingKB(),
        apiGetEmployees(),
        apiGetDepartments(),
        apiGetDocumentTemplates(),
        apiGetDefaultDocumentTemplateBundle(),
      ]);
      setStats(s);
      setTemplates(tmpl);
      setSessions(sess);
      setKbArticles(kb);
      setEmployees(emps);
      setDepartments(depts);
      setDocumentTemplates(docTemplates);
      setDefaultDocumentBundle(docBundle);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Lightweight stats refresh — does NOT trigger the full-page loading spinner.
  // Used after task completion so the pie chart updates without closing the session dialog.
  const refreshStats = useCallback(async () => {
    try {
      const [s, sess] = await Promise.all([apiGetOnboardingStats(), apiGetOnboardingSessions()]);
      setStats(s);
      setSessions(sess);
    } catch { /* silently ignore */ }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!stats) return;
    const targets = { active: stats.activeSessions, completed: stats.completedSessions, rate: stats.completionRate, avg: stats.avgDurationDays };
    const duration = 800;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setAnimatedStats({
        active: Math.round(targets.active * ease),
        completed: Math.round(targets.completed * ease),
        rate: Math.round(targets.rate * ease),
        avg: Math.round(targets.avg * ease),
      });
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [stats]);

  useEffect(() => {
    if (!newSessionDialog) {
      setSessionForm({ employeeId: 0, templateId: 0, startDate: "", buddyId: 0, notes: "" });
      setSessionDocumentOverrideEnabled(false);
      setSessionDocumentTemplateIds([]);
    }
  }, [newSessionDialog]);

  const toggleSessionDocumentTemplate = (templateId: number) => {
    setSessionDocumentTemplateIds((current) =>
      current.includes(templateId)
        ? current.filter((id) => id !== templateId)
        : [...current, templateId],
    );
  };

  const renderDocumentAutomationOptions = () => (
    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-foreground">{t("onboarding.documentAutomation")}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {defaultDocumentBundle?.templateIds?.length
              ? t("onboarding.defaultDocumentBundleConfigured", { count: defaultDocumentBundle.templateIds.length })
              : t("onboarding.noDefaultDocumentBundle")}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Label className="text-xs text-muted-foreground">{t("onboarding.override")}</Label>
          <Switch
            checked={sessionDocumentOverrideEnabled}
            onCheckedChange={(checked) => {
              setSessionDocumentOverrideEnabled(checked);
              if (!checked) setSessionDocumentTemplateIds([]);
            }}
          />
        </div>
      </div>

      {sessionDocumentOverrideEnabled && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {t("onboarding.documentOverrideDescription")}
          </p>
          {documentTemplates.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
              {t("onboarding.noDocumentTemplates")}
            </div>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
              {documentTemplates.map((template) => {
                const checked = sessionDocumentTemplateIds.includes(template.id);
                return (
                  <label
                    key={template.id}
                    htmlFor={`session-document-template-${template.id}`}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      checked
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/40 hover:bg-muted/20"
                    }`}
                  >
                    <Checkbox
                      id={`session-document-template-${template.id}`}
                      checked={checked}
                      onCheckedChange={() => toggleSessionDocumentTemplate(template.id)}
                      className="mt-0.5"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">{template.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {template.templateType.charAt(0).toUpperCase() +
                          template.templateType.slice(1)}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const sessionDocumentOverrideInvalid =
    sessionDocumentOverrideEnabled &&
    documentTemplates.length > 0 &&
    sessionDocumentTemplateIds.length === 0;

  // ── Template handlers ──

  const openTemplate = async (tmpl: OnboardingTemplate) => {
    setSelectedTemplate(tmpl);
    setTemplateSheet(true);
    setEditTemplateMode(false);
    setTmplEditForm({ name: tmpl.name, description: tmpl.description || "", targetRole: tmpl.targetRole || "", targetDepartment: tmpl.targetDepartment || "" });
    try {
      const tasks = await apiGetTemplateTasks(tmpl.id);
      setTemplateTasks(tasks);
    } catch { setTemplateTasks([]); }
  };

  const handleCreateTemplate = async () => {
    if (!tmplForm.name) return;
    try {
      await apiCreateOnboardingTemplate(tmplForm);
      toast({ title: t("onboarding.templateCreated") });
      setNewTemplateDialog(false);
      setTmplForm({ name: "", description: "", targetRole: "", targetDepartment: "" });
      loadData();
    } catch (e: any) {
      toast({ title: t("common.error"), description: taskErrorMessage(e), variant: "destructive" });
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    try {
      await apiDeleteOnboardingTemplate(id);
      toast({ title: t("onboarding.templateDeleted") });
      setTemplateSheet(false);
      loadData();
    } catch (e: any) {
      toast({ title: t("common.error"), description: taskErrorMessage(e), variant: "destructive" });
    }
  };

  const handleSaveTemplateTask = async () => {
    if (!selectedTemplate || !taskForm.title) return;
    try {
      if (editingTask) {
        await apiUpdateTemplateTask(selectedTemplate.id, editingTask.id, taskForm);
      } else {
        await apiCreateTemplateTask(selectedTemplate.id, taskForm);
      }
      const tasks = await apiGetTemplateTasks(selectedTemplate.id);
      setTemplateTasks(tasks);
      setTaskDialog(false);
      setEditingTask(null);
      setTaskForm({ title: "", description: "", category: "custom", dayOffset: 0, isRequired: true, assigneeType: "new_hire", taskType: "task", documentTemplateIds: [], sortOrder: 0 });
      loadData();
    } catch (e: any) {
      toast({ title: t("common.error"), description: taskErrorMessage(e), variant: "destructive" });
    }
  };

  const openTaskEdit = (task: OnboardingTemplateTask) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title,
      description: task.description || "",
      category: task.category,
      dayOffset: task.dayOffset,
      isRequired: task.isRequired,
      assigneeType: task.assigneeType,
      taskType: task.taskType || "task",
      documentTemplateIds: task.documentTemplateIds || [],
      sortOrder: task.sortOrder,
    });
    setTaskDialog(true);
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate || !tmplEditForm.name) return;
    try {
      const updated = await apiUpdateOnboardingTemplate(selectedTemplate.id, tmplEditForm);
      setSelectedTemplate({ ...selectedTemplate, ...updated });
      setEditTemplateMode(false);
      loadData();
      toast({ title: t("onboarding.templateSaved") });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    if (!selectedTemplate) return;
    try {
      await apiDeleteTemplateTask(selectedTemplate.id, taskId);
      setTemplateTasks((prev) => prev.filter((t) => t.id !== taskId));
      loadData();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  // ── Session handlers ──

  const openSession = async (s: OnboardingSession) => {
    try {
      const full = await apiGetOnboardingSession(s.id);
      setSelectedSession(full);
      setSessionSheet(true);
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleCreateSession = async () => {
    if (!sessionForm.employeeId) return;
    try {
      await apiCreateOnboardingSession({
        employeeId: sessionForm.employeeId,
        templateId: sessionForm.templateId || undefined,
        startDate: sessionForm.startDate || undefined,
        buddyId: sessionForm.buddyId || undefined,
        notes: sessionForm.notes || undefined,
        documentTemplateIds: sessionDocumentOverrideEnabled
          ? sessionDocumentTemplateIds
          : undefined,
      });
      toast({ title: t("onboarding.sessionStarted") });
      setNewSessionDialog(false);
      loadData();
    } catch (e: any) {
      toast({ title: t("common.error"), description: taskErrorMessage(e), variant: "destructive" });
    }
  };

  const fuzzyMatch = (haystack: string, needle: string): number => {
    const h = haystack.toLowerCase();
    const n = needle.toLowerCase();
    if (h === n) return 1;
    if (h.includes(n)) return 0.8;
    const words = n.split(/\s+/);
    const matched = words.filter((w) => h.includes(w)).length;
    return matched / Math.max(words.length, 1);
  };

  const handleUpdateTaskStatus = async (sessionId: number, taskId: number, status: string) => {
    try {
      const result = await apiUpdateOnboardingTask(sessionId, taskId, { status });
      if (selectedSession) {
        setSelectedSession({
          ...selectedSession,
          progress: result.progress,
          status: result.sessionStatus,
          tasks: selectedSession.tasks.map((task) => (task.id === taskId
            ? { ...task, status: result.status, completedAt: result.completedAt, notes: result.notes, documentIds: result.documentIds }
            : task)),
        });
      }
      // Refresh stats + sessions in background so the pie chart updates
      // without triggering the full-page loading spinner (which would close the dialog).
      refreshStats();
    } catch (e: any) {
      toast({ title: t("common.error"), description: taskErrorMessage(e), variant: "destructive" });
    }
  };

  const handleAddAdHocTask = async () => {
    if (!selectedSession || !adHocTaskForm.title) return;
    try {
      const newTask = await apiAddOnboardingTask(selectedSession.id, adHocTaskForm);
      setSelectedSession({
        ...selectedSession,
        expectedEndDate: newTask.expectedEndDate ?? selectedSession.expectedEndDate,
        tasks: [...selectedSession.tasks, newTask],
      });
      setAdHocTaskForm({ title: "", description: "", category: "custom", dueDate: "", assigneeType: "new_hire" });
      toast({ title: t("onboarding.taskAdded") });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleCancelSession = async (id: number) => {
    try {
      await apiDeleteOnboardingSession(id);
      toast({ title: t("onboarding.sessionCancelled") });
      setSessionSheet(false);
      loadData();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  // ── KB handlers ──

  const handleSaveArticle = async () => {
    try {
      const payload = { ...kbForm, tags: kbForm.tags ? kbForm.tags.split(",").map((t) => t.trim()) : [] };
      if (selectedArticle) {
        await apiUpdateOnboardingKBArticle(selectedArticle.id, payload);
        toast({ title: t("onboarding.articleSaved") });
      } else {
        await apiCreateOnboardingKBArticle(payload);
        toast({ title: t("onboarding.articleCreated") });
      }
      setKbSheet(false);
      setSelectedArticle(null);
      setKbForm({ title: "", content: "", category: "", tags: "", isPublished: true });
      loadData();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleKBFileUpload = async (files: FileList) => {
    if (files.length === 0) return;
    setKbUploading(true);
    try {
      let article = selectedArticle;
      // Auto-create article if it doesn't exist yet
      if (!article) {
        const title = kbForm.title || files[0].name.replace(/\.[^/.]+$/, "");
        const payload = { ...kbForm, title, tags: kbForm.tags ? kbForm.tags.split(",").map((t) => t.trim()) : [], isPublished: kbForm.isPublished };
        article = await apiCreateOnboardingKBArticle(payload);
        setSelectedArticle(article);
        setKbForm((f) => ({ ...f, title }));
        setKbArticles((prev) => [article!, ...prev]);
      }
      const updated = await apiUploadKBFiles(article.id, Array.from(files));
      setSelectedArticle(updated);
      setKbArticles((prev) => prev.map((a) => a.id === updated.id ? updated : a));
      toast({ title: t("onboarding.filesUploaded") });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setKbUploading(false);
    }
  };

  const handleKBFileDelete = async (fileIndex: number) => {
    if (!selectedArticle) return;
    try {
      const updated = await apiDeleteKBFile(selectedArticle.id, fileIndex);
      setSelectedArticle(updated);
      setKbArticles((prev) => prev.map((a) => a.id === updated.id ? updated : a));
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleDeleteArticle = async (id: number) => {
    try {
      await apiDeleteOnboardingKBArticle(id);
      toast({ title: t("onboarding.articleDeleted") });
      loadData();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  // ── Task detail handlers (admin) ──

  const openAdminTaskDetail = async (task: OnboardingTask) => {
    setActiveAdminTask(task);
    setAdminTaskNotes(task.notes || "");
    setAdminTaskStatus(task.status);
    setAdminTaskDocs([]);
    setTaskDetailOpen(true);
    if (task.documentIds?.length) {
      try {
        const docs = await Promise.all(task.documentIds.map((documentId) => apiGetHRDocument(documentId)));
        setAdminTaskDocs(docs);
      } catch { setAdminTaskDocs([]); }
    }
  };

  const handleSaveAdminTask = async () => {
    if (!selectedSession || !activeAdminTask) return;
    setAdminTaskSaving(true);
    try {
      const result = await apiUpdateOnboardingTask(selectedSession.id, activeAdminTask.id, {
        status: adminTaskStatus,
        notes: adminTaskNotes || undefined,
      });
      setSelectedSession({
        ...selectedSession,
        progress: result.progress,
        status: result.sessionStatus,
        tasks: selectedSession.tasks.map((t) =>
          t.id === activeAdminTask.id
            ? { ...t, status: result.status, completedAt: result.completedAt, notes: result.notes, documentIds: result.documentIds }
            : t
        ),
      });
      setTaskDetailOpen(false);
      refreshStats();
    } catch (e: any) {
      toast({ title: t("common.error"), description: taskErrorMessage(e), variant: "destructive" });
    } finally {
      setAdminTaskSaving(false);
    }
  };

  const openArticle = (a: OnboardingKBArticle | null) => {
    setSelectedArticle(a);
    setKbForm(a ? { title: a.title, content: a.content, category: a.category || "", tags: (a.tags || []).join(", "), isPublished: a.isPublished } : { title: "", content: "", category: "", tags: "", isPublished: true });
    setKbSheet(true);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-6 w-full">
          <PageHeaderSkeleton />
          <StatCardsSkeleton count={4} />
          <CardGridSkeleton count={2} className="grid grid-cols-1 md:grid-cols-2 gap-6" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
              <UserPlus className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{t("onboarding.title")}</h1>
              <p className="text-sm text-muted-foreground mt-1">{t("onboarding.subtitle")}</p>
            </div>
          </div>
          <Button onClick={() => setNewSessionDialog(true)}>
            <UserPlus className="h-4 w-4 mr-2" />{t("onboarding.startOnboarding")}
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
            <TabsTrigger value="dashboard" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"><LayoutDashboard className="h-4 w-4 mr-1" />{t("onboarding.dashboardTab")}</TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"><ClipboardList className="h-4 w-4 mr-1" />{t("onboarding.templatesTab")}</TabsTrigger>
            <TabsTrigger value="sessions" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"><Users className="h-4 w-4 mr-1" />{t("onboarding.sessionsTab")}</TabsTrigger>
            <TabsTrigger value="kb" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"><BookOpen className="h-4 w-4 mr-1" />{t("onboarding.kbTab")}</TabsTrigger>
            <TabsTrigger value="offboarding" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"><LogOut className="h-4 w-4 mr-1" />{t("onboarding.offboardingTab")}</TabsTrigger>
          </TabsList>

          {/* ── Dashboard ── */}
          <TabsContent value="dashboard" className="space-y-6 mt-4">
            <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="grid grid-cols-2 sm:grid-cols-4 sm:divide-x divide-border/60 divide-y sm:divide-y-0">
                {[
                  { label: t("onboarding.activeOnboardings"), value: animatedStats.active, icon: Play, bg: "bg-green-500/10", color: "text-green-600", action: () => { setTab("sessions"); setSessionFilter("active"); } },
                  { label: t("onboarding.completedOnboardings"), value: animatedStats.completed, icon: CheckCircle2, bg: "bg-blue-500/10", color: "text-blue-600", action: () => { setTab("sessions"); setSessionFilter("completed"); } },
                  { label: t("onboarding.completionRate"), value: `${animatedStats.rate}%`, icon: TrendingUp, bg: "bg-purple-500/10", color: "text-purple-600", action: undefined as (() => void) | undefined },
                  { label: t("onboarding.avgDuration"), value: `${animatedStats.avg}d`, icon: Clock, bg: "bg-amber-500/10", color: "text-amber-600", action: undefined as (() => void) | undefined },
                ].map((s, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-3.5 px-5 py-4 ${s.action ? "cursor-pointer hover:bg-muted/30 transition-colors" : ""}`}
                    onClick={s.action}
                  >
                    <div className={`h-9 w-9 rounded-xl ${s.bg} flex items-center justify-center shrink-0`}>
                      <s.icon className={`h-4 w-4 ${s.color}`} />
                    </div>
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="text-xs font-medium text-muted-foreground">{s.label}</span>
                      <p className="text-2xl font-bold tabular-nums text-foreground leading-none">{s.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle className="text-base">{t("onboarding.progressDistribution")}</CardTitle></CardHeader>
                <CardContent>
                  {stats && stats.progressDistribution.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie data={stats.progressDistribution} dataKey="count" nameKey="range" cx="50%" cy="50%" outerRadius={90} label={({ range, count }) => `${range}: ${count}`}>
                          {stats.progressDistribution.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-60 flex items-center justify-center text-muted-foreground text-sm">{t("onboarding.noData")}</div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">{t("onboarding.recentActivity")}</CardTitle></CardHeader>
                <CardContent>
                  {stats && stats.recentSessions.length > 0 ? (
                    <div className="space-y-3">
                      {stats.recentSessions.map((rs) => {
                        const sc = statusConfig[rs.status] || statusConfig.active;
                        const Icon = sc.icon;
                        return (
                          <div key={rs.id} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors cursor-pointer" onClick={() => openSession(rs as any)}>
                            <Icon className="h-4 w-4 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{rs.employeeName}</p>
                              <p className="text-xs text-muted-foreground">{rs.templateName || t("onboarding.custom")}</p>
                            </div>
                            <Progress value={rs.progress} className="w-20 h-2" />
                            <span className="text-xs font-mono">{rs.progress}%</span>
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="h-60 flex items-center justify-center text-muted-foreground text-sm">{t("onboarding.noData")}</div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ── Templates ── */}
          <TabsContent value="templates" className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">{templates.length} {t("onboarding.templatesCount")}</p>
              <Button size="sm" onClick={() => { setTmplForm({ name: "", description: "", targetRole: "", targetDepartment: "" }); setNewTemplateDialog(true); }}>
                <Plus className="h-4 w-4 mr-1" />{t("onboarding.newTemplate")}
              </Button>
            </div>

            {templates.length === 0 ? (
              <Card><CardContent className="p-12 text-center text-muted-foreground">
                <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-40" />
                <p>{t("onboarding.noTemplates")}</p>
              </CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map((tmpl) => (
                  <Card key={tmpl.id} className="cursor-pointer hover:shadow-md transition-all hover:-translate-y-0.5" onClick={() => openTemplate(tmpl)}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold">{tmpl.name}</h3>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{tmpl.description || t("onboarding.noDescription")}</p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                      </div>
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {tmpl.targetRole && <Badge variant="secondary">{tmpl.targetRole}</Badge>}
                        {tmpl.targetDepartment && <Badge variant="outline">{tmpl.targetDepartment}</Badge>}
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span><ClipboardList className="h-3 w-3 inline mr-1" />{tmpl.taskCount} {t("onboarding.tasks")}</span>
                        <span><Users className="h-3 w-3 inline mr-1" />{tmpl.activeSessions} {t("onboarding.activeLabel")}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ── Sessions ── */}
          <TabsContent value="sessions" className="space-y-4 mt-4">
            {/* Filter bar */}
            {sessions.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                {(["all", "active", "completed", "paused", "cancelled"] as const).map((f) => {
                  const count = f === "all" ? sessions.length : sessions.filter((s) => s.status === f).length;
                  const active = sessionFilter === f;
                  return (
                    <button
                      key={f}
                      onClick={() => setSessionFilter(f)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                      }`}
                    >
                      <span className="capitalize">{f === "all" ? t("common.all") : t(`onboarding.status${f.charAt(0).toUpperCase() + f.slice(1)}` as any) || f}</span>
                      <span className={`${active ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-muted-foreground"} rounded-full px-1.5 py-0.5 text-[10px] leading-none`}>{count}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {(() => {
              const filtered = sessionFilter === "all" ? sessions : sessions.filter((s) => s.status === sessionFilter);
              if (sessions.length === 0) return (
                <Card><CardContent className="p-12 text-center text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-40" />
                  <p>{t("onboarding.noSessions")}</p>
                </CardContent></Card>
              );
              return (
                <>
                  <div className="text-xs text-muted-foreground">{filtered.length} {t("onboarding.sessionsTab").toLowerCase()}</div>
                  <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
                          <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("onboarding.colEmployee", "Employee")}</TableHead>
                          <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("onboarding.colTemplate", "Template")}</TableHead>
                          <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden md:table-cell">{t("onboarding.colBuddy", "Buddy")}</TableHead>
                          <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden lg:table-cell">{t("onboarding.colStarted", "Started")}</TableHead>
                          <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("onboarding.colProgress", "Progress")}</TableHead>
                          <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("organization.status", "Status")}</TableHead>
                          <TableHead className="w-10" />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="py-12">
                              <div className="flex flex-col items-center gap-3 text-center">
                                <Users className="h-8 w-8 text-muted-foreground/40" />
                                <p className="text-sm text-muted-foreground">{t("onboarding.noSessionsForFilter")}</p>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : filtered.map((s) => {
                          const sc = statusConfig[s.status] || statusConfig.active;
                          const SIcon = sc.icon;
                          const initials = s.employeeName.split(" ").filter(Boolean).map((n) => n[0] || "").join("").toUpperCase().slice(0, 2);
                          return (
                            <TableRow
                              key={s.id}
                              className="border-b border-border/40 transition-colors cursor-pointer hover:bg-muted/40"
                              onClick={() => openSession(s)}
                            >
                              <TableCell className="pl-4 py-3">
                                <div className="flex items-center gap-3">
                                  <EmployeeAvatar
                                    avatarUrl={null}
                                    avatarColor="from-indigo-400 to-blue-500"
                                    initials={initials}
                                    size="sm"
                                    className="shrink-0"
                                  />
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium truncate">{s.employeeName}</p>
                                    <p className="text-xs text-muted-foreground truncate">{s.employeeRole || s.employeeDepartment || "—"}</p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="py-3">
                                <span className="text-sm text-foreground">{s.templateName || t("onboarding.custom")}</span>
                              </TableCell>
                              <TableCell className="py-3 hidden md:table-cell">
                                {s.buddyName ? (
                                  <div className="flex items-center gap-1.5">
                                    <EmployeeAvatar
                                      avatarUrl={s.buddyAvatarUrl ?? null}
                                      avatarColor={s.buddyAvatarColor || "from-blue-400 to-indigo-500"}
                                      initials={s.buddyName.split(" ").filter(Boolean).map((n) => n[0] || "").join("").toUpperCase()}
                                      size="xs"
                                    />
                                    <span className="text-xs text-muted-foreground">{s.buddyName}</span>
                                  </div>
                                ) : <span className="text-sm text-muted-foreground">—</span>}
                              </TableCell>
                              <TableCell className="py-3 hidden lg:table-cell">
                                <span className="text-sm text-muted-foreground">
                                  {formattedDate(s.startDate)}
                                </span>
                              </TableCell>
                              <TableCell className="py-3">
                                <div className="flex items-center gap-2">
                                  <Progress value={s.progress} className="w-20 h-1.5" />
                                  <span className="text-xs font-mono text-muted-foreground">{s.progress}%</span>
                                </div>
                              </TableCell>
                              <TableCell className="py-3">
                                <Badge variant="outline" className={`text-[10px] border ${sc.color}`}>
                                  <SIcon className="h-3 w-3 mr-0.5" />{statusLabel(s.status)}
                                </Badge>
                              </TableCell>
                              <TableCell className="py-3 w-10">
                                <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              );
            })()}
          </TabsContent>

          {/* ── Knowledge Base ── */}
          <TabsContent value="kb" className="space-y-4 mt-4">
            {/* Context banner */}
            <div className="flex gap-3 rounded-xl border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800 p-4">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-200">{t("onboarding.kbContextTitle")}</p>
                <p className="text-xs text-blue-700 dark:text-blue-300">{t("onboarding.kbContextDesc")}</p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">{kbArticles.length} {t("onboarding.articlesCount")}</div>
              <Button size="sm" onClick={() => openArticle(null)}>
                <Plus className="h-4 w-4 mr-1" />{t("onboarding.newArticle")}
              </Button>
            </div>

            {kbArticles.length === 0 ? (
              <Card><CardContent className="p-12 text-center text-muted-foreground">
                <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-40" />
                <p>{t("onboarding.noArticles")}</p>
              </CardContent></Card>
            ) : (
              <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
                      <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("onboarding.colArticle", "Article")}</TableHead>
                      <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden md:table-cell">{t("onboarding.colCategory", "Category")}</TableHead>
                      <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden lg:table-cell">{t("onboarding.colTags", "Tags")}</TableHead>
                      <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("organization.status", "Status")}</TableHead>
                      <TableHead className="w-20" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {kbArticles.map((a) => (
                      <TableRow key={a.id} className="border-b border-border/40 transition-colors hover:bg-muted/40">
                        <TableCell className="pl-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{a.title}</p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                {(a.fileNames?.length || 0) > 0 && (
                                  <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                                    <Paperclip className="h-3 w-3" />{a.fileNames.length}
                                  </span>
                                )}
                                {a.hasExtractedText && <Badge className="bg-purple-100 text-purple-700 text-[10px] px-1 py-0">AI</Badge>}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="py-3 hidden md:table-cell">
                          <span className="text-sm text-muted-foreground">{a.category || t("onboarding.general")}</span>
                        </TableCell>
                        <TableCell className="py-3 hidden lg:table-cell">
                          <span className="text-xs text-muted-foreground">{(a.tags || []).join(", ") || "—"}</span>
                        </TableCell>
                        <TableCell className="py-3">
                          {a.isPublished
                            ? <Badge className="bg-green-100 text-green-700 text-[10px] border border-green-200">{t("onboarding.published")}</Badge>
                            : <Badge variant="outline" className="text-[10px]">{t("onboarding.draft")}</Badge>}
                        </TableCell>
                        <TableCell className="py-3">
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); openArticle(a); }}>
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); handleDeleteArticle(a.id); }}>
                              <Trash2 className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* ── Offboarding ── */}
          <TabsContent value="offboarding" className="mt-4">
            <OffboardingPanel />
          </TabsContent>
        </Tabs>
      </div>

      {/* ── Template Dialog ── */}
      <Dialog open={templateSheet} onOpenChange={setTemplateSheet}>
        <DialogContent className="sm:max-w-2xl max-h-[88vh] flex flex-col gap-0 p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <DialogTitle className="flex items-center justify-between">
              <span>{selectedTemplate?.name}</span>
              {selectedTemplate && !editTemplateMode && (
                <Button variant="ghost" size="sm" onClick={() => setEditTemplateMode(true)}>
                  <Edit className="h-3.5 w-3.5 mr-1" />{t("common.edit")}
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedTemplate && (
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {editTemplateMode ? (
                <div className="space-y-3 p-4 rounded-lg border bg-secondary/30">
                  <div><Label>{t("onboarding.templateName")}</Label><Input value={tmplEditForm.name} onChange={(e) => setTmplEditForm({ ...tmplEditForm, name: e.target.value })} /></div>
                  <div><Label>{t("onboarding.description")}</Label><Textarea value={tmplEditForm.description} onChange={(e) => setTmplEditForm({ ...tmplEditForm, description: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>{t("onboarding.targetRole")}</Label>
                      <Select value={tmplEditForm.targetRole || "__none__"} onValueChange={(v) => setTmplEditForm({ ...tmplEditForm, targetRole: v === "__none__" ? "" : v })}>
                        <SelectTrigger className="mt-1"><SelectValue placeholder={t("onboarding.anyRole")} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">{t("onboarding.anyRole")}</SelectItem>
                          {Array.from(new Set(employees.map((e) => e.jobTitle).filter(Boolean))).sort().map((pos) => (
                            <SelectItem key={pos!} value={pos!}>{pos}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t("onboarding.targetDepartment")}</Label>
                      <Select value={tmplEditForm.targetDepartment || "__none__"} onValueChange={(v) => setTmplEditForm({ ...tmplEditForm, targetDepartment: v === "__none__" ? "" : v })}>
                        <SelectTrigger className="mt-1"><SelectValue placeholder={t("onboarding.anyDepartment")} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">{t("onboarding.anyDepartment")}</SelectItem>
                          {departments.map((d) => (
                            <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSaveTemplate} disabled={!tmplEditForm.name}><Save className="h-3.5 w-3.5 mr-1" />{t("common.save")}</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditTemplateMode(false)}>{t("common.cancel")}</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex gap-2 flex-wrap">
                    {selectedTemplate.targetRole && <Badge variant="secondary">{selectedTemplate.targetRole}</Badge>}
                    {selectedTemplate.targetDepartment && <Badge variant="outline">{selectedTemplate.targetDepartment}</Badge>}
                  </div>
                  {selectedTemplate.description && <p className="text-sm text-muted-foreground">{selectedTemplate.description}</p>}
                </>
              )}

              <div className="flex justify-between items-center">
                <h4 className="text-sm font-semibold">{t("onboarding.tasks")} ({templateTasks.length})</h4>
                <Button size="sm" variant="outline" onClick={() => { setEditingTask(null); setTaskForm({ title: "", description: "", category: "custom", dayOffset: 0, isRequired: true, assigneeType: "new_hire", taskType: "task", documentTemplateIds: [], sortOrder: 0 }); setTaskDialog(true); }}>
                  <Plus className="h-3 w-3 mr-1" />{t("onboarding.addTask")}
                </Button>
              </div>

              <div className="space-y-2">
                {templateTasks.map((task) => {
                  const ttc = taskTypeConfig[task.taskType] || taskTypeConfig.task;
                  const TIcon = ttc.icon;
                  return (
                    <div key={task.id} className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:shadow-sm transition-all cursor-pointer group" onClick={() => openTaskEdit(task)}>
                      <div className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 mt-0.5 ${ttc.color}`}>
                        <TIcon className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{task.title}</span>
                          {task.isRequired && <Badge variant="destructive" className="text-[10px] px-1.5">{t("onboarding.required")}</Badge>}
                        </div>
                        <div className="flex gap-2 mt-1 flex-wrap">
                          <Badge className={`${categoryConfig[task.category]?.color || "bg-gray-100 text-gray-700"} text-[10px]`}>{categoryLabel(task.category)}</Badge>
                          <Badge className={`${ttc.color} text-[10px]`}>{taskTypeLabel(task.taskType)}</Badge>
                          <span className="text-xs text-muted-foreground">{t("onboarding.dayLabel", { day: task.dayOffset })}</span>
                          <span className="text-xs text-muted-foreground">{assigneeLabel(task.assigneeType)}</span>
                        </div>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); openTaskEdit(task); }}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="pt-4 border-t">
                <Button variant="destructive" size="sm" onClick={() => handleDeleteTemplate(selectedTemplate.id)}>
                  <Trash2 className="h-4 w-4 mr-1" />{t("onboarding.deleteTemplate")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Session Dialog ── */}
      <Dialog open={sessionSheet} onOpenChange={setSessionSheet}>
        <DialogContent className="sm:max-w-2xl max-h-[88vh] flex flex-col gap-0 p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <DialogTitle>{selectedSession?.employeeName}</DialogTitle>
          </DialogHeader>
          {selectedSession && (
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div className="flex items-center gap-3">
                {(() => { const sc = statusConfig[selectedSession.status] || statusConfig.active; const I = sc.icon; return <Badge className={sc.color}><I className="h-3 w-3 mr-1" />{statusLabel(selectedSession.status)}</Badge>; })()}
                <Badge variant="outline">{selectedSession.templateName || t("onboarding.custom")}</Badge>
                {selectedSession.buddyName && (
                  <div className="flex items-center gap-2 px-2 py-1 bg-secondary rounded-full">
                    <EmployeeAvatar
                      avatarUrl={selectedSession.buddyAvatarUrl}
                      avatarColor={selectedSession.buddyAvatarColor || "from-blue-400 to-indigo-500"}
                      initials={selectedSession.buddyName.split(" ").filter(Boolean).map(n => (n[0] || "")).join("").toUpperCase()}
                      size="xs"
                    />
                    <span className="text-xs font-medium">{selectedSession.buddyName}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                <Progress value={selectedSession.progress} className="flex-1 h-3" />
                <span className="text-lg font-bold">{selectedSession.progress}%</span>
              </div>

              <div className="text-sm text-muted-foreground">
                {t("onboarding.started")}: {formattedDate(selectedSession.startDate)}
                {selectedSession.expectedEndDate && ` · ${t("onboarding.expectedEnd")}: ${formattedDate(selectedSession.expectedEndDate)}`}
              </div>

              {/* Tasks grouped by category */}
              {CATEGORIES.map((cat) => {
                const catTasks = selectedSession.tasks.filter((t) => t.category === cat);
                if (catTasks.length === 0) return null;
                return (
                  <div key={cat}>
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <Badge className={categoryConfig[cat]?.color || ""}>{categoryLabel(cat)}</Badge>
                      <span className="text-xs text-muted-foreground">{catTasks.filter((t) => t.status === "completed").length}/{catTasks.length}</span>
                    </h4>
                    <div className="space-y-2">
                      {catTasks.map((task) => (
                        <div key={task.id} className="flex items-center gap-3 p-3 rounded-lg border bg-card cursor-pointer hover:shadow-sm transition-all" onClick={() => openAdminTaskDetail(task)}>
                          <button
                            className={`h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors shrink-0 ${task.status === "completed" ? "bg-green-500 border-green-500 text-white" : "border-muted-foreground/30 hover:border-green-500"}`}
                            onClick={(e) => { e.stopPropagation(); handleUpdateTaskStatus(selectedSession.id, task.id, task.status === "completed" ? "pending" : "completed"); }}
                            disabled={selectedSession.status !== "active"}
                          >
                            {task.status === "completed" && <CheckCircle2 className="h-3 w-3" />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${task.status === "completed" ? "line-through text-muted-foreground" : ""}`}>{task.title}</p>
                            <div className="flex gap-2 mt-0.5 flex-wrap">
                              {task.status === "completed" && task.completedAt ? (
                                <span className="text-xs text-green-600 flex items-center gap-1">
                                  <CheckCircle2 className="h-3 w-3" />
                                  {t("onboarding.completedOn")} {formattedDate(task.completedAt)}
                                </span>
                              ) : (
                                task.dueDate && <span className="text-xs text-muted-foreground">{t("onboarding.due")}: {formattedDate(task.dueDate)}</span>
                              )}
                              {(task.documentIds?.length || 0) > 0 && <span className="text-xs text-blue-600 flex items-center gap-1"><Paperclip className="h-3 w-3" />{task.documentIds.length}</span>}
                              {task.notes && <span className="text-xs text-muted-foreground italic truncate max-w-32">{task.notes}</span>}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            {(() => { const ttc = taskTypeConfig[task.taskType] || taskTypeConfig.task; return <Badge className={`${ttc.color} text-[10px]`}>{taskTypeLabel(task.taskType)}</Badge>; })()}
                            <span className="text-xs text-muted-foreground">
                              {task.assigneeName
                                ? `${task.assigneeName} (${assigneeLabel(task.assigneeType)})`
                                : assigneeLabel(task.assigneeType)}
                            </span>
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {selectedSession.status === "active" && (
                <div className="pt-4 border-t space-y-3">
                  <h4 className="text-sm font-semibold">{t("onboarding.addAdHocTask")}</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder={t("onboarding.taskTitle")} value={adHocTaskForm.title} onChange={(e) => setAdHocTaskForm({ ...adHocTaskForm, title: e.target.value })} />
                    <Select value={adHocTaskForm.category} onValueChange={(v) => setAdHocTaskForm({ ...adHocTaskForm, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{categoryLabel(c)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button size="sm" onClick={handleAddAdHocTask} disabled={!adHocTaskForm.title}><Plus className="h-3 w-3 mr-1" />{t("onboarding.addTask")}</Button>
                </div>
              )}

              {selectedSession.status === "active" && (
                <div className="pt-4 border-t">
                  <Button variant="destructive" size="sm" onClick={() => handleCancelSession(selectedSession.id)}>
                    {t("onboarding.cancelSession")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── New Template Dialog ── */}
      <Dialog open={newTemplateDialog} onOpenChange={setNewTemplateDialog}>
        <DialogContent className="">
          <DialogHeader><DialogTitle>{t("onboarding.newTemplate")}</DialogTitle></DialogHeader>
          <div>
            <FloatField label={t("onboarding.templateName")} required value={tmplForm.name} onChange={(v) => setTmplForm({ ...tmplForm, name: v })} />
            <FloatField label={t("onboarding.description")} type="textarea" value={tmplForm.description} onChange={(v) => setTmplForm({ ...tmplForm, description: v })} />
            <div className="flex gap-3">
              <FloatWrapper label={t("onboarding.targetRole")} className="flex-1">
                <Select value={tmplForm.targetRole} onValueChange={(v) => setTmplForm({ ...tmplForm, targetRole: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4"><SelectValue placeholder={t("onboarding.anyRole")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t("onboarding.anyRole")}</SelectItem>
                    {Array.from(new Set(employees.map((e) => e.jobTitle).filter(Boolean))).sort().map((pos) => (
                      <SelectItem key={pos!} value={pos!}>{pos}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FloatWrapper>
              <FloatWrapper label={t("onboarding.targetDepartment")} className="flex-1">
                <Select value={tmplForm.targetDepartment} onValueChange={(v) => setTmplForm({ ...tmplForm, targetDepartment: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4"><SelectValue placeholder={t("onboarding.anyDepartment")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t("onboarding.anyDepartment")}</SelectItem>
                    {departments.map((d) => (<SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </FloatWrapper>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewTemplateDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleCreateTemplate} disabled={!tmplForm.name}>{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── New Session Dialog ── */}
      <Dialog open={newSessionDialog} onOpenChange={setNewSessionDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("onboarding.startOnboarding")}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <FloatWrapper label={t("onboarding.selectEmployee")} required>
              <EmployeeCombobox
                employees={employees}
                value={String(sessionForm.employeeId || "")}
                onChange={(v) => { const empId = parseInt(v); setSessionForm((f) => ({ ...f, employeeId: empId, buddyId: f.buddyId === empId ? 0 : f.buddyId })); }}
                placeholder={t("onboarding.selectEmployee")}
              />
            </FloatWrapper>
            <FloatWrapper label={t("onboarding.selectTemplate")}>
              <Select value={String(sessionForm.templateId || "")} onValueChange={(v) => setSessionForm({ ...sessionForm, templateId: parseInt(v) })}>
                <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4"><SelectValue placeholder={t("onboarding.optionalTemplate")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">{t("onboarding.noTemplate")}</SelectItem>
                  {templates.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </FloatWrapper>
            <div className="flex gap-3">
              <FloatWrapper label={t("onboarding.startDate")} className="flex-1">
                <DatePicker value={sessionForm.startDate} onChange={(v) => setSessionForm({ ...sessionForm, startDate: v })} className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 text-sm" />
              </FloatWrapper>
              <FloatWrapper label={t("onboarding.assignBuddy")} className="flex-1">
                <EmployeeCombobox
                  employees={employees}
                  value={String(sessionForm.buddyId || "")}
                  onChange={(v) => setSessionForm({ ...sessionForm, buddyId: parseInt(v) })}
                  placeholder={t("onboarding.optionalBuddy")}
                  excludeIds={sessionForm.employeeId ? [sessionForm.employeeId] : []}
                />
              </FloatWrapper>
            </div>
            <FloatField label={t("onboarding.notes")} type="textarea" value={sessionForm.notes} onChange={(v) => setSessionForm({ ...sessionForm, notes: v })} />
          </div>
          {renderDocumentAutomationOptions()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewSessionDialog(false)}>{t("common.cancel")}</Button>
            <Button
              onClick={handleCreateSession}
              disabled={!sessionForm.employeeId || sessionDocumentOverrideInvalid}
            >
              <Play className="h-4 w-4 mr-1" />{t("onboarding.startOnboarding")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add/Edit Task to Template Dialog ── */}
      <Dialog open={taskDialog} onOpenChange={(open) => { setTaskDialog(open); if (!open) setEditingTask(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingTask ? t("onboarding.editTask") : t("onboarding.addTask")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("onboarding.taskTitle")}</Label><Input value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} /></div>
            <div><Label>{t("onboarding.description")}</Label><Textarea value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} /></div>
            <div>
              <Label>{t("onboarding.taskType")}</Label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {TASK_TYPES.map((tt) => {
                  const ttc = taskTypeConfig[tt];
                  const TIcon = ttc.icon;
                  return (
                    <button
                      key={tt}
                      type="button"
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all text-sm ${taskForm.taskType === tt ? "border-primary bg-primary/5 font-medium" : "border-transparent bg-secondary/50 hover:border-muted-foreground/20"}`}
                      onClick={() => setTaskForm({
                        ...taskForm,
                        taskType: tt,
                        documentTemplateIds: tt === "document_upload" ? taskForm.documentTemplateIds : [],
                      })}
                    >
                      <TIcon className="h-5 w-5" />
                      {taskTypeLabel(tt)}
                    </button>
                  );
                })}
              </div>
            </div>
            {taskForm.taskType === "document_upload" && (
              <div className="space-y-2">
                <Label>{t("onboarding.relevantDocumentTemplates")}</Label>
                <p className="text-xs text-muted-foreground">{t("onboarding.relevantDocumentTemplatesDesc")}</p>
                {documentTemplates.length > 0 ? (
                  <div className="max-h-36 overflow-y-auto rounded-lg border p-2 space-y-1">
                    {documentTemplates.map((template) => {
                      const checked = taskForm.documentTemplateIds.includes(template.id);
                      return (
                        <label key={template.id} className="flex items-center gap-2 rounded-md p-2 hover:bg-muted/50 cursor-pointer">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => setTaskForm((current) => ({
                              ...current,
                              documentTemplateIds: checked
                                ? current.documentTemplateIds.filter((id) => id !== template.id)
                                : [...current.documentTemplateIds, template.id],
                            }))}
                          />
                          <span className="text-sm">{template.name}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t("onboarding.noDocumentTemplates")}</p>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("onboarding.category")}</Label>
                <Select value={taskForm.category} onValueChange={(v) => setTaskForm({ ...taskForm, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{categoryLabel(c)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("onboarding.assigneeType")}</Label>
                <Select value={taskForm.assigneeType} onValueChange={(v) => setTaskForm({ ...taskForm, assigneeType: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ASSIGNEE_TYPES.map((a) => <SelectItem key={a} value={a}>{assigneeLabel(a)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("onboarding.dayOffset")}</Label><Input type="number" min={0} value={taskForm.dayOffset} onChange={(e) => setTaskForm({ ...taskForm, dayOffset: parseInt(e.target.value) || 0 })} /></div>
              <div className="flex items-center gap-2 pt-6"><Switch checked={taskForm.isRequired} onCheckedChange={(v) => setTaskForm({ ...taskForm, isRequired: v })} /><Label>{t("onboarding.required")}</Label></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTaskDialog(false); setEditingTask(null); }}>{t("common.cancel")}</Button>
            <Button onClick={handleSaveTemplateTask} disabled={!taskForm.title}>{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── KB Article Dialog ── */}
      <Dialog open={kbSheet} onOpenChange={setKbSheet}>
        <DialogContent className="sm:max-w-lg max-h-[88vh] flex flex-col gap-0 p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <DialogTitle>{selectedArticle ? t("onboarding.editArticle") : t("onboarding.newArticle")}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div><Label>{t("onboarding.articleTitle")}</Label><Input value={kbForm.title} onChange={(e) => setKbForm({ ...kbForm, title: e.target.value })} /></div>
            <div><Label>{t("onboarding.articleContent")}</Label><Textarea className="min-h-[120px] font-mono text-sm" placeholder={t("onboarding.articleContentPlaceholder")} value={kbForm.content} onChange={(e) => setKbForm({ ...kbForm, content: e.target.value })} /></div>

            {/* Document upload area — always visible */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2"><Upload className="h-4 w-4" />{t("onboarding.kbDocuments")}</Label>
              <p className="text-xs text-muted-foreground">{t("onboarding.kbDocumentsDesc")}</p>

              <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-6 cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors">
                <Upload className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{kbUploading ? t("onboarding.uploading") : t("onboarding.dropDocuments")}</span>
                <span className="text-xs text-muted-foreground">{t("onboarding.kbFileTypesHint")}</span>
                <input
                  type="file"
                  className="hidden"
                  multiple
                  accept=".pdf,.txt,.md,.doc,.docx"
                  disabled={kbUploading}
                  onChange={(e) => e.target.files && handleKBFileUpload(e.target.files)}
                />
              </label>

              {selectedArticle && (selectedArticle.fileNames?.length || 0) > 0 && (
                <div className="space-y-2">
                  {selectedArticle.fileNames.map((name, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-secondary/50 text-sm">
                      <File className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="flex-1 truncate">{name}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleKBFileDelete(idx)}>
                        <X className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                  ))}
                  {selectedArticle.hasExtractedText && (
                    <p className="text-xs text-purple-600 flex items-center gap-1"><Sparkles className="h-3 w-3" />{t("onboarding.textExtracted")}</p>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("onboarding.category")}</Label><Input value={kbForm.category} onChange={(e) => setKbForm({ ...kbForm, category: e.target.value })} /></div>
              <div><Label>{t("onboarding.tags")}</Label><Input placeholder="tag1, tag2" value={kbForm.tags} onChange={(e) => setKbForm({ ...kbForm, tags: e.target.value })} /></div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={kbForm.isPublished} onCheckedChange={(v) => setKbForm({ ...kbForm, isPublished: v })} />
              <Label>{t("onboarding.published")}</Label>
            </div>
            <Button onClick={handleSaveArticle} disabled={!kbForm.title} className="w-full">{t("common.save")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Admin Task Detail Dialog ── */}
      <Dialog open={taskDetailOpen} onOpenChange={setTaskDetailOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{activeAdminTask?.title}</DialogTitle></DialogHeader>
          {activeAdminTask && (
            <div className="space-y-4">
              {activeAdminTask.description && (
                <p className="text-sm text-muted-foreground p-3 rounded-lg bg-secondary/50">{activeAdminTask.description}</p>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">{t("onboarding.category")}: </span>
                  <Badge className={categoryConfig[activeAdminTask.category]?.color || ""}>{categoryLabel(activeAdminTask.category)}</Badge>
                </div>
                {activeAdminTask.completedAt ? (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                    <span>{t("onboarding.completedOn")} {formattedDate(activeAdminTask.completedAt)}</span>
                  </div>
                ) : activeAdminTask.dueDate ? (
                  <div>
                    <span className="text-muted-foreground">{t("onboarding.due")}: </span>
                    <span className="font-medium">{formattedDate(activeAdminTask.dueDate)}</span>
                  </div>
                ) : null}
              </div>

              <div className="text-sm">
                <span className="text-muted-foreground">{t("onboarding.assignee")}: </span>
                <span className="font-medium">
                  {activeAdminTask.assigneeName
                    ? `${activeAdminTask.assigneeName} (${assigneeLabel(activeAdminTask.assigneeType)})`
                    : assigneeLabel(activeAdminTask.assigneeType)}
                </span>
              </div>

              <div>
                <Label className="mb-2 block">{t("onboarding.taskStatus")}</Label>
                <Select value={adminTaskStatus} onValueChange={setAdminTaskStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">{t("onboarding.statusPending")}</SelectItem>
                    <SelectItem value="in_progress">{t("onboarding.statusInProgress")}</SelectItem>
                    <SelectItem value="completed">{t("onboarding.statusCompleted")}</SelectItem>
                    <SelectItem value="skipped">{t("onboarding.statusSkipped")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-2 block">{t("onboarding.notes")}</Label>
                <Textarea placeholder={t("onboarding.taskNotesPlaceholder")} value={adminTaskNotes} onChange={(e) => setAdminTaskNotes(e.target.value)} className="min-h-[80px]" />
              </div>

              {(activeAdminTask.taskType === "document_upload" || activeAdminTask.documentIds.length > 0) && <div>
                <Label className="mb-2 block flex items-center gap-2"><FileText className="h-4 w-4" />{t("onboarding.linkedDocuments")}</Label>
                {adminTaskDocs.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {adminTaskDocs.map((doc) => (
                      <div key={doc.id} className="flex items-center gap-3 p-2 rounded-lg border">
                        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm truncate">{doc.documentName}</p>
                          <p className="text-xs text-muted-foreground">{t(`documents.status.${doc.status}`, doc.status)}</p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          aria-label={t("onboarding.openLinkedDocument")}
                          onClick={() => navigate(`/documents?openDocument=${doc.id}`)}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t("onboarding.noLinkedDocuments")}</p>
                )}
              </div>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskDetailOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSaveAdminTask} disabled={adminTaskSaving}>
              <Save className="h-4 w-4 mr-1" />{adminTaskSaving ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
